import type { BoxProps } from '@chakra-ui/react';
import {
  Box,
  Button,
  HStack,
  Image,
  Link,
  Text,
  VStack,
} from '@chakra-ui/react';
import { AGENT_TOKEN_DECIMALS } from '@cronos-app/sdk';
import { currentWallet } from '@cronos-app/wallet';
import { MaskIcon } from '@ui/components/MaskIcon';
import { Num } from '@ui/components/Num';
import { PercentChange } from '@ui/components/Num/PercentChange';
import { UsdValue } from '@ui/components/Num/UsdValue';
import { EXTERNAL_LINK } from '@ui/config/externalLinks';
import { Token, useNormalTokenConfig } from '@ui/config/token';
import type { NormalToken } from '@ui/config/token/types';
import type { UserAgent } from '@ui/hooks/api/useUserAgents';
import { useTokenBalance } from '@ui/hooks/contracts/useTokenBalance';
import { useTokenPrice } from '@ui/hooks/tokenPrice';
import { useGlobalChainLayer } from '@ui/hooks/useGlobalChainLayer';
import { useWatchAsset } from '@ui/hooks/useWatchAsset';

import { useIsMyAccount } from '../../useIsMyAccount';
import { useProfileDrawer } from '../../useProfileDrawer';
import { TokenListRowInfo } from './TokenListRowInfo';

type TokenListRowProps = BoxProps & {
  token?: NormalToken;
  userAgent?: UserAgent;
};
export const TokenListRow = ({
  token,
  userAgent,
  ...props
}: TokenListRowProps) => {
  const account = useProfileDrawer((s) => s.account);
  const isMyAccount = useIsMyAccount();
  const chainLayer = useGlobalChainLayer();
  const { watchAsset, canWatch } = useWatchAsset();

  const normalTokenConfig = useNormalTokenConfig();
  const normalTokenInfo = token ? normalTokenConfig[token] : null;

  const balance = useTokenBalance({
    account,
    tokenAddress:
      userAgent?.agent_token_address ||
      userAgent?.bonding_token_address ||
      normalTokenInfo?.address,
  }).data;

  const tokenPrice = useTokenPrice({
    token: !userAgent
      ? token
      : userAgent.agent_token_address
      ? {
          type: Token.Agent,
          agentAddress: userAgent.agent_token_address,
        }
      : {
          type: Token.Bonding,
          bondingAddress: userAgent.bonding_token_address,
        },
  }).data;

  return (
    <Box
      py="12px"
      borderColor="secondary.orange"
      borderBottomWidth="1px"
      {...props}
    >
      <HStack align="flex-start">
        <VStack gap="2px" alignItems="flex-start" maxW="50%">
          <HStack gap="4px">
            {userAgent?.image_url && (
              <Image
                src={userAgent.image_url}
                boxSize="16px"
                rounded="full"
                alt={userAgent.symbol}
              />
            )}
            {normalTokenInfo?.Icon && (
              <normalTokenInfo.Icon boxSize="16px" rounded="full" />
            )}
            <Text textStyle="body2" lineClamp={1} wordBreak="break-all">
              {userAgent?.symbol || normalTokenInfo?.symbol}
            </Text>
            <PercentChange
              value={tokenPrice?.usd_price_change_24h}
              textStyle="caption"
            />
          </HStack>
          <HStack>
            {canWatch && (
              <Button
                hidden={!token && !userAgent?.agent_token_address}
                variant="tertiary"
                textStyle="body2Bold"
                onClick={() => {
                  if (token) {
                    watchAsset({
                      token,
                    });
                  }
                  if (userAgent?.agent_token_address) {
                    currentWallet.watchAsset({
                      symbol: userAgent.symbol,
                      address: userAgent.agent_token_address,
                      decimals: AGENT_TOKEN_DECIMALS,
                      image: userAgent.image_url,
                    });
                  }
                }}
              >
                <MaskIcon icon="url(/masks/add.svg)" boxSize="16px" />
                Wallet
              </Button>
            )}
            {isMyAccount && token === Token.agentFun && balance?.lte(0) && (
              <Button
                asChild
                variant="tertiary"
                alignSelf="center"
                textStyle="body2Bold"
              >
                <Link
                  href={EXTERNAL_LINK.swapAgentFun(chainLayer)}
                  target="_blank"
                >
                  <MaskIcon
                    icon="url(/masks/arrow-outward.svg)"
                    boxSize="16px"
                  />
                  Buy
                </Link>
              </Button>
            )}
          </HStack>
        </VStack>
        <VStack align="flex-end" gap="2px" maxW="50%" ml="auto">
          <Num
            value={balance}
            textStyle="body2Bold"
            lineClamp={1}
            wordBreak="break-all"
          />
          <UsdValue
            prefix="~ $"
            value={balance}
            price={tokenPrice?.usd_price}
            textStyle="body3"
            lineClamp={1}
            wordBreak="break-all"
          />
        </VStack>
      </HStack>
      {userAgent && <TokenListRowInfo userAgent={userAgent} mt="10px" />}
    </Box>
  );
};
